import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaSearch, FaCalendar, FaUser, FaSpinner, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useWordPress } from '../hooks/useWordPress';
import { FEATURED_TAG_SLUGS } from '../config/featured';
import './BlogPage.css';

const BlogPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get('category');
  const tagParam = searchParams.get('tag');

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(categoryParam || 'all');
  const [selectedTag, setSelectedTag] = useState(tagParam || 'all');
  const [currentPage, setCurrentPage] = useState(1);
  const [isSearching, setIsSearching] = useState(false);

  const { usePosts, useCategories, useTags, useSearchPosts } = useWordPress();

  const { data: categoriesData, isLoading: categoriesLoading } = useCategories();
  const { data: tagsData, isLoading: tagsLoading } = useTags();

  // The featured-carousel tags are homepage plumbing, not blog topics
  const visibleTags = (tagsData || []).filter(
    tag => !FEATURED_TAG_SLUGS.includes(tag.slug)
  );

  const {
    data: searchData,
    isLoading: searchLoading,
    error: searchError
  } = useSearchPosts(searchTerm, currentPage, 6);

  const {
    data: postsData,
    isLoading: postsLoading,
    error: postsError
  } = usePosts(currentPage, 6, selectedCategory, selectedTag);

  const hasSearchTerm = searchTerm.length > 2;
  const finalData = hasSearchTerm ? searchData : postsData;
  const finalLoading = hasSearchTerm ? searchLoading : postsLoading;
  const finalError = hasSearchTerm ? searchError : postsError;

  useEffect(() => {
    if (categoryParam) setSelectedCategory(categoryParam);
    if (tagParam) setSelectedTag(tagParam);
  }, [categoryParam, tagParam]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, selectedTag]);

  useEffect(() => {
    const timer = setTimeout(() => setIsSearching(false), 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setIsSearching(true);
  };

  const decodeHTMLEntities = (text) => {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  };

  const categories = [
    { id: 'all', name: 'All Posts' },
    ...(categoriesData || []).map(cat => ({ id: cat.id.toString(), name: cat.name }))
  ];

  const formatPosts = (posts) => {
    if (!posts) return [];
    return posts.map(post => {
      const featuredImage = post._embedded?.['wp:featuredmedia']?.[0]?.source_url;
      const author = post._embedded?.author?.[0]?.name || 'Unknown Author';
      const postCategories = post._embedded?.['wp:term']?.[0] || [];
      const excerptText = decodeHTMLEntities(
        post.content.rendered
          .replace(/<h[1-6][^>]*>.*?<\/h[1-6]>/gi, '')
          .replace(/<[^>]*>/g, '')
          .trim()
      ).substring(0, 200);

      return {
        id: post.id,
        title: post.title.rendered,
        excerpt: excerptText,
        image: featuredImage || 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=400&h=250&fit=crop',
        date: post.date,
        author,
        category: postCategories[0]?.name || 'Uncategorized',
        readTime: `${Math.ceil(post.content.rendered.split(' ').length / 200)} min read`,
        slug: post.slug
      };
    });
  };

  const posts = formatPosts(finalData?.posts || []);
  const pagination = finalData?.pagination || {};
  const isLoading = finalLoading || categoriesLoading || tagsLoading || isSearching;

  const handleTagClick = (tagId) => {
    setSelectedTag(tagId);
    setSelectedCategory('all');
    setSearchParams(tagId === 'all' ? {} : { tag: tagId });
  };

  const totalPosts = pagination.totalPosts || posts.length;
  const perPage = 6;
  const showingFrom = totalPosts === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const showingTo = Math.min(currentPage * perPage, totalPosts);

  return (
    <div className="blog-page">
      {/* Hero Banner */}
      <div className="blog-hero">
        <div className="container">
          <h1>Diving Blog</h1>
          <p>Read posts from my dive trips around the world</p>
        </div>
      </div>

      <div className="container blog-body">
        {/* Filter Bar */}
        <div className="filter-bar">
          <div className="filter-bar-search">
            <FaSearch className="filter-search-icon" />
            <input
              type="text"
              placeholder="Search articles..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="filter-search-input"
            />
            {isSearching && <FaSpinner className="filter-spinner" />}
          </div>

          <div className="filter-bar-divider" />

          <div className="filter-bar-select-wrap">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="filter-category-select"
              disabled={categoriesLoading}
            >
              {categoriesLoading ? (
                <option>Loading...</option>
              ) : (
                categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))
              )}
            </select>
          </div>
        </div>

        {/* Tags */}
        {!tagsLoading && visibleTags.length > 0 && (
          <div className="tags-bar">
            <button
              className={`tag-badge ${selectedTag === 'all' ? 'active' : ''}`}
              onClick={() => handleTagClick('all')}
            >
              All Tags
            </button>
            {visibleTags.map(tag => (
              <button
                key={tag.id}
                className={`tag-badge ${selectedTag === tag.id.toString() ? 'active' : ''}`}
                onClick={() => handleTagClick(tag.id.toString())}
              >
                {tag.name}
                <span className="tag-count">{tag.count}</span>
              </button>
            ))}
          </div>
        )}

        {/* Post count */}
        {!isLoading && !finalError && totalPosts > 0 && (
          <p className="post-count">
            Showing {showingFrom}–{showingTo} of {totalPosts} posts
          </p>
        )}

        {/* Loading */}
        {isLoading && (
          <div className="loading-container">
            <FaSpinner className="loading-spinner" />
            <p>Loading posts...</p>
          </div>
        )}

        {/* Error */}
        {finalError && (
          <div className="error-container">
            <h3>Error loading posts</h3>
            <p>Please check your WordPress connection and try again.</p>
          </div>
        )}

        {/* Blog Grid */}
        {!isLoading && !finalError && (
          <div className="blog-grid">
            {posts.map((post, index) => (
              <motion.article
                key={post.id}
                className="blog-card"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Link to={`/blog/${post.slug}`} className="blog-card-image">
                  <img src={post.image} alt={post.title} />
                  <div className="blog-card-category">{post.category}</div>
                </Link>

                <div className="blog-card-content">
                  <div className="blog-card-meta">
                    <span className="blog-card-date">
                      <FaCalendar />
                      {new Date(post.date).toLocaleDateString()}
                    </span>
                    <span className="blog-card-author">
                      <FaUser />
                      {post.author}
                    </span>
                  </div>

                  <Link to={`/blog/${post.slug}`} className="blog-card-title-link">
                    <h3 className="blog-card-title">{post.title}</h3>
                  </Link>
                  <p className="blog-card-excerpt">{post.excerpt}</p>

                  <div className="blog-card-footer">
                    <span className="read-time">{post.readTime}</span>
                    <Link to={`/blog/${post.slug}`} className="read-more">
                      Read More →
                    </Link>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        )}

        {/* Pagination */}
        {!isLoading && !finalError && pagination.totalPages > 1 && (
          <div className="pagination">
            <button
              className="pagination-btn pagination-nav"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              aria-label="Previous page"
            >
              <FaChevronLeft />
            </button>

            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </button>
            ))}

            <button
              className="pagination-btn pagination-nav"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === pagination.totalPages}
              aria-label="Next page"
            >
              <FaChevronRight />
            </button>
          </div>
        )}

        {/* No Results */}
        {!isLoading && !finalError && posts.length === 0 && (
          <div className="no-results">
            <h3>No articles found</h3>
            <p>Try adjusting your search terms or filters to find what you're looking for.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogPage;
